import { useQuery, UseQueryResult, useQueryClient, UseMutationResult, useMutation } from '@tanstack/react-query';

import { TaskCard, TaskTypeCategory, TaskTypes, TaskData } from '../@types/task.type';
import { Folders } from '../@types/uploaded-files.type';
import { createAndUpdateTask, createTaskServiceLogByApprove, deleteTaskById, getTaskByServiceId, getTaskTypes, TaskApproveData, TaskApproveResponse } from '../api/task';
import { ServiceLogAllKey } from '../configs/services';
import { TaskTypeKey, TaskTypesKey } from '../configs/task';
import { UploadedFilesKey } from '../configs/uploaded-files';

// Mutation input - can be partial task card with additional fields
interface TaskMutationInput {
  taskTypeCode: string;
  taskTypeCategory?: TaskTypeCategory;
  serviceId: number;
  taskData?: TaskData;
  taskId?: number;
  execute?: boolean;
}

export const useGetTaskByServiceId = (serviceId: number): UseQueryResult<TaskCard[]> => {
  return useQuery({
    queryKey: [TaskTypeKey, serviceId],
    queryFn: () => getTaskByServiceId(serviceId),
    enabled: false
  });
};

export const useCreateAndUpdateTask = (serviceId: number): UseMutationResult<TaskCard, Error, TaskMutationInput> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: TaskMutationInput) => {
      return await createAndUpdateTask(data as unknown as TaskCard);
    },

    onSuccess: async (response, taskData) => {
      await queryClient.invalidateQueries({
        queryKey: [TaskTypeKey, serviceId]
      });
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey, serviceId]
      });

      if (response && taskData.taskTypeCategory === TaskTypeCategory.ServiceSpecial) {
        await queryClient.invalidateQueries({
          queryKey: [UploadedFilesKey, serviceId, Folders.ServiceSpecialFileAttachments]
        });
      }
    }
  });
};

export const useCreateTaskServiceLogByApprove = (serviceId: number): UseMutationResult<TaskApproveResponse, Error, TaskApproveData> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: TaskApproveData) => {
      return await createTaskServiceLogByApprove(data.id, data);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [ServiceLogAllKey, serviceId]
      });
    }
  });
};

// Delete task mutation data
interface DeleteTaskData {
  id: number;
}

export const useDeleteTaskById = (serviceId: number): UseMutationResult<void, Error, DeleteTaskData> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: DeleteTaskData) => {
      return await deleteTaskById(data.id);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [TaskTypeKey, serviceId]
      });
    }
  });
};

export const useGetTaskTypes = (serviceId: number): UseQueryResult<TaskTypes[]> => {
  return useQuery({
    queryKey: [TaskTypesKey, serviceId],
    queryFn: () => getTaskTypes(serviceId),
    enabled: false
  });
};